import {Component, inject, Signal, ViewChild} from '@angular/core';
import {LayerService} from '../../../../services/layer.service';
import {toSignal} from '@angular/core/rxjs-interop';
import {map} from 'rxjs/operators';
import {MapGroup} from '../../../../shared/classes/map-group';
import {MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatFormField, MatHint, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {tap} from 'rxjs';
import {MatDrawer, MatDrawerContainer, MatDrawerContent, MatSidenav} from '@angular/material/sidenav';
import {ModifyMapLayerComponent} from './edit-sections/modify-map-layer/modify-map-layer.component';
import {MapLayer} from '../../../../shared/classes/map-layer';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelDescription, MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import {MatTooltip} from '@angular/material/tooltip';
import { SNACKBAR_DURATION } from '../../../../shared/constants/snackbar.constants';

interface EditableLayerGroup extends MapGroup {
  expanded: boolean;
  editing: boolean
}

@Component({
  selector: 'tgrid-layer-management',
  imports: [
    MatIconButton,
    MatIcon,
    MatFormField,
    MatInput,
    MatHint,
    MatLabel,
    ReactiveFormsModule,
    MatDrawerContainer,
    MatDrawerContent,
    MatDrawer,
    ModifyMapLayerComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    MatExpansionPanelHeader,
    MatTooltip,
  ],
  templateUrl: './layer-management.component.html',
  standalone: true,
  styleUrl: './layer-management.component.scss',
})
export class LayerManagementComponent {
  @ViewChild('drawer', {static: true}) drawer: MatSidenav;


  private layerService = inject(LayerService);
  private snackBar = inject(MatSnackBar);


  protected editFormGroup = new FormGroup({});

  protected layerGroups: Signal<EditableLayerGroup[]> = toSignal(this.layerService.getLayerGroups().pipe(
    map(layerGroups => {
      return layerGroups.map(layerGroup => {
        return {
          expanded: false,
          editing: false,
          ...layerGroup,
        };
      });
    }),
  ), {initialValue: []});

  protected selectedLayer: MapLayer;

  private addGroupControls(layerGroup: MapGroup): void {
    this.editFormGroup.addControl(`${layerGroup.uiHandle}-name`, new FormControl(layerGroup.displayName));
    this.editFormGroup.addControl(`${layerGroup.uiHandle}-order`, new FormControl(layerGroup.ordering));
  }

  private clearGroupControls(layerGroup: MapGroup): void {
    this.editFormGroup.removeControl(`${layerGroup.uiHandle}-name`, {emitEvent: false});
    this.editFormGroup.removeControl(`${layerGroup.uiHandle}-name`, {emitEvent: false});
  }

  protected saveGroupChanges(layerGroup: EditableLayerGroup) {

    const tmpGroup = new MapGroup(layerGroup);
    tmpGroup.displayName = this.editFormGroup.get(`${layerGroup.uiHandle}-name`)?.value as unknown as string;
    tmpGroup.ordering = this.editFormGroup.get(`${layerGroup.uiHandle}-order`)?.value as unknown as number;


    this.layerService.updateLayerGroup(tmpGroup)
      .pipe(
        map(updatedLayerGroup => {
          return {
            editing: layerGroup.editing,
            expanded: layerGroup.expanded,
            ...updatedLayerGroup,
          } as EditableLayerGroup;
        }),
        tap(() => {
          this.layerGroups().sort((a, b) => a.id - b.id);
        }),
      )
      .subscribe({
        next: (updatedLayerGroup) => {
          const oldIndex = this.layerGroups().findIndex(lg => lg.id === updatedLayerGroup.id);
          this.layerGroups().splice(oldIndex, 1, updatedLayerGroup);
          updatedLayerGroup.editing = false;

          this.clearGroupControls(updatedLayerGroup);

          this.snackBar.open('Group Updated Successfully', 'X', { duration: SNACKBAR_DURATION.MEDIUM });

        },
        error: () => {
          this.snackBar.open('Unable to Update Group', 'X', { duration: SNACKBAR_DURATION.MEDIUM });
        },
      });


  }

  protected cancelGroupChanges(layerGroup: EditableLayerGroup) {
    layerGroup.editing = false;
    this.clearGroupControls(layerGroup);
  }

  protected enableGroupEditing(layerGroup: EditableLayerGroup) {
    this.addGroupControls(layerGroup);
    layerGroup.editing = true;
  }

  public setSelectedLayer(layer: MapLayer) {
    this.selectedLayer = layer;
    this.drawer.open();
  }


 public handleLayerUpdate(updatedLayer: MapLayer) {
    const groupToUpdate = this.layerGroups()
      .find(lg => lg.id === updatedLayer.layerGroupId);

    if (!groupToUpdate) {
      return;
    }

    if (this.selectedLayer?.layerGroupId === updatedLayer.layerGroupId) {
      const indexToReplace = groupToUpdate.layers.findIndex(l => l.id === updatedLayer.id);
      groupToUpdate.layers.splice(indexToReplace, 1, updatedLayer);
    } else {
      groupToUpdate.layers.push(updatedLayer);

      const oldGroup = this.layerGroups().find(lg => lg.id === this.selectedLayer.layerGroupId);
      const indexToRemove = oldGroup?.layers.findIndex(l => l.id === this.selectedLayer.id) || -1;
      oldGroup?.layers.splice(indexToRemove, 1);
    }

    this.drawer.close();

  }
}
